import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageItemType, Playlist, TransitionType } from '../types';
import { Page } from './Page';
import { ThemeWrapper } from './ThemeWrapper';
import { ForceDisconnectButton } from './ForceDisconnectButton';
import { DeviceIdDialog } from './DeviceIdDialog';

interface PlayListProps {
  playlist: Playlist;
}

function PlayList({ playlist }: PlayListProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [theme, setTheme] = useState(playlist?.playlistItems?.[currentIndex]?.page?.theme?.css);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const sortedPlaylistItems = useMemo(
    () => [...(playlist?.playlistItems ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [playlist?.playlistItems],
  );
  const playlistLength = sortedPlaylistItems.length;
  const currentPlaylistItem = sortedPlaylistItems?.[currentIndex];
  const nextIndex = currentIndex === playlistLength - 1 ? 0 : currentIndex + 1;
  const nextPlaylistItem = sortedPlaylistItems?.[nextIndex];
  const currentHasVideo =
    currentPlaylistItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false;
  const nextHasVideo =
    nextPlaylistItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false;

  const transitionIn = sortedPlaylistItems?.[currentIndex]?.transitionIn ?? TransitionType.Ease;
  const useOpacityTransition = transitionIn !== 'linear' && !currentHasVideo && !nextHasVideo;

  const moveToIndex = useCallback(
    (fromIndex: number, toIndex: number) => {
      const currentItem = sortedPlaylistItems[fromIndex];
      const nextItem = sortedPlaylistItems[toIndex];
      const nextTheme = nextItem?.page?.theme?.css ?? undefined;
      const transitionHasVideo =
        (currentItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false) ||
        (nextItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false);
      const themeDelay = transitionHasVideo || currentItem?.transitionOut === 'linear' ? 0 : 50;

      setCurrentIndex(toIndex);

      if (nextItem?.page?.theme?.themeId !== currentItem?.page?.theme?.themeId) {
        setTimeout(() => {
          setTheme(nextTheme);
        }, themeDelay);
      }
    },
    [sortedPlaylistItems],
  );

  useEffect(() => {
    setCurrentIndex(0);
    setTheme(sortedPlaylistItems[0]?.page?.theme?.css);
    clearInterval(intervalRef.current);
  }, [playlist?.playlistId]);

  useEffect(() => {
    if (playlistLength === 0) {
      clearInterval(intervalRef.current);
      return;
    }

    const playlistItem = sortedPlaylistItems?.[currentIndex];
    const duration = playlistItem?.duration ?? 10;

    const interval = setInterval(() => {
      const toIndex = currentIndex === playlistLength - 1 ? 0 : currentIndex + 1;
      moveToIndex(currentIndex, toIndex);

      clearInterval(intervalRef.current);
    }, duration * 1000);

    intervalRef.current = interval;

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [currentIndex, playlistLength, sortedPlaylistItems, moveToIndex]);

  return (
    <>
      <div className="w-full h-screen bg-black">
        {sortedPlaylistItems?.map((playlistItem, index) => {
          const page = playlistItem?.page;
          const isActivePage = index === currentIndex;
          const wrapperIdentity =
            playlistItem?.playlistItemId !== undefined
              ? playlistItem.playlistItemId.toString()
              : `${playlistItem?.pageId ?? 'page'}_${index}`;
          const wrapperId = `page_wrapper_${wrapperIdentity}`;

          const pageTheme = isActivePage && useOpacityTransition ? theme : page?.theme?.css;
          let resolvedThemeConfig: { theme?: { background?: string } } | null = null;
          try {
            resolvedThemeConfig = (typeof pageTheme === 'string' ? JSON.parse(pageTheme) : pageTheme) as { theme?: { background?: string } } | null;
          } catch {
            // Malformed theme JSON — skip background extraction.
          }
          const themeBackground = resolvedThemeConfig?.theme?.background;
          const pageBackground = themeBackground
            ? themeBackground.startsWith('http') ? `url(${themeBackground})` : themeBackground
            : undefined;

          return (
            <div
              id={wrapperId}
              className={`
              absolute top-0 left-0 w-full h-full
              ${isActivePage ? 'opacity-100  z-10' : 'opacity-0 z-0'}
              ${useOpacityTransition ? 'duration-1000 transition-opacity ' : ' '}
            `}
              style={{ background: pageBackground, backgroundSize: 'cover', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat' }}
              key={wrapperIdentity}
            >
              <ThemeWrapper key={wrapperIdentity} config={pageTheme as any}>
                {page && (
                  <Page
                    page={page}
                    isActive={isActivePage}
                  />
                )}
              </ThemeWrapper>
            </div>
          );
        })}
      </div>
      <DeviceIdDialog />
      <ForceDisconnectButton />
    </>
  );
}

export default PlayList;
